/**
 * Facturen Service - API calls voor facturatie
 */

import apiFetch, { getApiBaseUrl } from './api';

export type FactuurStatus = 'CONCEPT' | 'VERSTUURD' | 'BETAALD' | 'DEELS_BETAALD' | 'VERVALLEN' | 'GEANNULEERD' | 'CREDITNOTA';

export interface FactuurRegel {
  id?: number;
  factuur_id?: number;
  volgnummer: number;
  type_regel: string;
  voorraad_item_id?: number;
  omschrijving: string;
  aantal: number;
  eenheid?: string;
  prijs_per_eenheid: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  btw_percentage: number;
  totaal: number;
  actief?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface FactuurBetaling {
  id?: number;
  factuur_id?: number;
  datum: string;
  bedrag: number;
  betaalmethode?: string;
  referentie?: string;
  omschrijving?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Factuur {
  id: number;
  factuurnummer: string;
  relatie_id: number;
  boot_id?: number;
  werkorder_id?: number;
  offerte_id?: number;
  status: FactuurStatus;
  factuur_datum: string;
  vervaldatum: string;
  contactpersoon?: string;
  subtotaal: number;
  btw_percentage: number;
  btw_bedrag: number;
  totaal: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  bedrag_betaald: number;
  bedrag_openstaand: number;
  laatste_betaling_datum?: string;
  betalingstermijn: number;
  betalingsvoorwaarden?: string;
  onderwerp: string;
  omschrijving?: string;
  interne_notities?: string;
  verstuurd_op?: string;
  verstuurd_naar_email?: string;
  credit_voor_factuur_id?: number;
  aangemaakt_door_user_id?: number;
  actief: boolean;
  created_at: string;
  updated_at?: string;
  regels: FactuurRegel[];
  betalingen: FactuurBetaling[];
}

export interface FactuurListItem {
  id: number;
  factuurnummer: string;
  relatie_id: number;
  boot_id?: number;
  werkorder_id?: number;
  status: FactuurStatus;
  factuur_datum: string;
  vervaldatum: string;
  onderwerp: string;
  totaal: number;
  bedrag_betaald: number;
  bedrag_openstaand: number;
  created_at: string;
  relatie_naam?: string;
  boot_naam?: string;
  werkordernummer?: string;
}

export interface FactuurCreateData {
  relatie_id: number;
  boot_id?: number;
  werkorder_id?: number;
  offerte_id?: number;
  factuur_datum: string;
  vervaldatum: string;
  contactpersoon?: string;
  subtotaal?: number;
  btw_percentage?: number;
  btw_bedrag?: number;
  totaal?: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  betalingstermijn?: number;
  betalingsvoorwaarden?: string;
  onderwerp: string;
  omschrijving?: string;
  interne_notities?: string;
  regels?: Omit<FactuurRegel, 'id' | 'factuur_id' | 'created_at' | 'updated_at'>[];
}

export interface FactuurCreateFromWerkorderData {
  werkorder_id: number;
  factuur_datum?: string;
  betalingstermijn?: number;
  interne_notities?: string;
}

export interface FactuurCreateFromTimeRegistrationsData {
  werkorder_id: number;
  tijdregistratie_ids: number[];
  factuur_datum?: string;
  betalingstermijn?: number;
  interne_notities?: string;
}

export interface FactuurUpdateData {
  relatie_id?: number;
  boot_id?: number;
  status?: FactuurStatus;
  factuur_datum?: string;
  vervaldatum?: string;
  contactpersoon?: string;
  subtotaal?: number;
  btw_percentage?: number;
  btw_bedrag?: number;
  totaal?: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  betalingstermijn?: number;
  betalingsvoorwaarden?: string;
  onderwerp?: string;
  omschrijving?: string;
  interne_notities?: string;
  actief?: boolean;
}

export interface BetalingCreateData {
  datum: string;
  bedrag: number;
  betaalmethode?: string;
  referentie?: string;
  omschrijving?: string;
}

class FacturenService {
  private baseUrl = '/api/v1/facturen';

  async getAll(filters?: {
    status?: FactuurStatus;
    relatie_id?: number;
    werkorder_id?: number;
  }): Promise<FactuurListItem[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.relatie_id) params.append('relatie_id', filters.relatie_id.toString());
    if (filters?.werkorder_id) params.append('werkorder_id', filters.werkorder_id.toString());

    const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
    return apiFetch(url);
  }

  async getById(id: number): Promise<Factuur> {
    return apiFetch(`${this.baseUrl}/${id}`);
  }

  async getByWerkorderId(werkorderId: number): Promise<FactuurListItem[]> {
    return this.getAll({ werkorder_id: werkorderId });
  }

  async create(data: FactuurCreateData): Promise<Factuur> {
    return apiFetch(this.baseUrl, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createFromWerkorder(data: FactuurCreateFromWerkorderData): Promise<Factuur> {
    return apiFetch(`${this.baseUrl}/from-werkorder`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createFromSelectedTimeRegistrations(data: FactuurCreateFromTimeRegistrationsData): Promise<Factuur> {
    return apiFetch(`${this.baseUrl}/from-time-registrations`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async update(id: number, data: FactuurUpdateData): Promise<Factuur> {
    return apiFetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(id: number): Promise<void> {
    return apiFetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE'
    });
  }

  // Regels
  async addRegel(factuurId: number, data: Omit<FactuurRegel, 'id' | 'factuur_id' | 'created_at' | 'updated_at'>): Promise<FactuurRegel> {
    return apiFetch(`${this.baseUrl}/${factuurId}/regels`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateRegel(factuurId: number, regelId: number, data: Partial<FactuurRegel>): Promise<FactuurRegel> {
    return apiFetch(`${this.baseUrl}/${factuurId}/regels/${regelId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteRegel(factuurId: number, regelId: number): Promise<void> {
    return apiFetch(`${this.baseUrl}/${factuurId}/regels/${regelId}`, {
      method: 'DELETE'
    });
  }

  // Betalingen
  async addBetaling(factuurId: number, data: BetalingCreateData): Promise<FactuurBetaling> {
    return apiFetch(`${this.baseUrl}/${factuurId}/betalingen`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteBetaling(factuurId: number, betalingId: number): Promise<void> {
    return apiFetch(`${this.baseUrl}/${factuurId}/betalingen/${betalingId}`, {
      method: 'DELETE'
    });
  }

  // Acties
  async verstuur(factuurId: number, email?: string): Promise<{ message: string; factuur_id: number; email: string }> {
    return apiFetch(`${this.baseUrl}/${factuurId}/verzenden`, {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async updateStatus(factuurId: number, status: FactuurStatus): Promise<Factuur> {
    return apiFetch(`${this.baseUrl}/${factuurId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status })
    });
  }

  async genereerPdf(factuurId: number): Promise<{ message: string; factuur_id: number; pdf_path: string }> {
    return apiFetch(`${this.baseUrl}/${factuurId}/genereer-pdf`, {
      method: 'POST'
    });
  }

  async downloadPdf(factuurId: number): Promise<Blob> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/facturen/${factuurId}/download-pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  async verplaatsPDF(factuurId: number): Promise<{success: boolean, path?: string}> {
    return await apiFetch(`${this.baseUrl}/${factuurId}/verplaats-pdf`, {
      method: 'POST'
    });
  }

  async getDocumentsPath(factuurId: number): Promise<{path: string}> {
    return await apiFetch(`${this.baseUrl}/${factuurId}/documents-path`);
  }
}

export const facturenService = new FacturenService();
